import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Map,
  useControl,
  type MapLayerMouseEvent,
  type MapRef,
} from 'react-map-gl/maplibre';
import { MapboxOverlay, type MapboxOverlayProps } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import { PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import 'maplibre-gl/dist/maplibre-gl.css';
import { env } from '../lib/env';
import { loadPopulationBlob } from '../lib/blobLoader';
import { assertAttributesAtIndexZero } from '../lib/attributes';
import { markFirstDot } from '../lib/perf';
import { useUiStore } from '../state/uiStore';
import { useBlobStore } from '../state/blobStore';
import { useSelectionStore } from '../state/selectionStore';
import { useFilteredIndices, useFilterStore } from '../state/filterStore';
import HoverTooltip, { type HoverInfo } from './HoverTooltip';

const SELECTED_ALPHA = 230;
const UNSELECTED_ALPHA = 30;
const COLOR_R = 96;
const COLOR_G = 165;
const COLOR_B = 250;
const SELECTED_COLOR_R = 251;
const SELECTED_COLOR_G = 146;
const SELECTED_COLOR_B = 60;

const INITIAL_VIEW_STATE = {
  longitude: -2.0,
  latitude: 52.85,
  zoom: 5.6,
  bearing: 0,
  pitch: 0,
};

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

export default function MapView() {
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const selectPerson = useUiStore((s) => s.selectPerson);
  const flyToTarget = useUiStore((s) => s.flyToTarget);
  const setFlyTo = useUiStore((s) => s.setFlyTo);
  const blob = useBlobStore((s) => s.blob);
  const loading = useBlobStore((s) => s.loading);
  const error = useBlobStore((s) => s.error);
  const setBlobInStore = useBlobStore((s) => s.setBlob);
  const setLoadingInStore = useBlobStore((s) => s.setLoading);
  const setErrorInStore = useBlobStore((s) => s.setError);
  const selectionMode = useSelectionStore((s) => s.mode);
  const polygon = useSelectionStore((s) => s.polygon);
  const baseSelection = useSelectionStore((s) => s.baseSelection);
  const startDrawing = useSelectionStore((s) => s.startDrawing);
  const addVertex = useSelectionStore((s) => s.addVertex);
  const closePolygon = useSelectionStore((s) => s.closePolygon);
  const clearSelection = useSelectionStore((s) => s.clearSelection);
  const filterStageCount = useFilterStore((s) => s.stages.length);
  const filteredResult = useFilteredIndices();
  const filterActive = baseSelection !== null || filterStageCount > 0;
  const mapRef = useRef<MapRef | null>(null);

  const fillColors = useMemo(() => {
    if (!blob) return null;
    const n = blob.data.length;
    const colors = new Uint8Array(n * 4);
    const baseAlpha = filterActive ? UNSELECTED_ALPHA : SELECTED_ALPHA;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      colors[o] = COLOR_R;
      colors[o + 1] = COLOR_G;
      colors[o + 2] = COLOR_B;
      colors[o + 3] = baseAlpha;
    }
    if (filterActive && filteredResult) {
      const indices = filteredResult.indices;
      for (let k = 0; k < indices.length; k++) {
        const o = indices[k] * 4;
        colors[o] = SELECTED_COLOR_R;
        colors[o + 1] = SELECTED_COLOR_G;
        colors[o + 2] = SELECTED_COLOR_B;
        colors[o + 3] = SELECTED_ALPHA;
      }
    }
    return colors;
  }, [blob, filterActive, filteredResult]);

  useEffect(() => {
    let cancelled = false;
    setLoadingInStore(true);
    setErrorInStore(null);
    loadPopulationBlob()
      .then((b) => {
        if (!cancelled) {
          if (import.meta.env.DEV) {
            try {
              assertAttributesAtIndexZero(b);
            } catch (err) {
              console.error(err);
            }
          }
          setBlobInStore(b);
          setLoadingInStore(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setErrorInStore(e instanceof Error ? e : new Error(String(e)));
          setLoadingInStore(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setBlobInStore, setLoadingInStore, setErrorInStore]);

  useEffect(() => {
    if (!flyToTarget) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      map.jumpTo({
        center: [flyToTarget.longitude, flyToTarget.latitude],
        zoom: flyToTarget.zoom,
      });
    } else {
      map.flyTo({
        center: [flyToTarget.longitude, flyToTarget.latitude],
        zoom: flyToTarget.zoom,
        essential: true,
      });
    }
    setFlyTo(null);
  }, [flyToTarget, setFlyTo]);

  useEffect(() => {
    if (!blob) return;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => markFirstDot());
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [blob]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
          return;
        }
      }
      if (e.code === 'Space') {
        const mode = useSelectionStore.getState().mode;
        if (mode !== 'drawing') {
          e.preventDefault();
          startDrawing();
        }
      } else if (e.key === 'Escape') {
        const mode = useSelectionStore.getState().mode;
        if (mode !== 'idle') {
          e.preventDefault();
          clearSelection();
        }
      } else if (e.key === 'Enter') {
        const state = useSelectionStore.getState();
        if (state.mode === 'drawing' && state.polygon.length >= 3 && blob) {
          e.preventDefault();
          closePolygon(blob.data.positions);
          const next = useSelectionStore.getState().baseSelection;
          console.info(`[selection] enclosed ${next?.length ?? 0} dots`);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [blob, startDrawing, clearSelection, closePolygon]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = selectionMode === 'drawing' ? 'crosshair' : '';
    return () => {
      canvas.style.cursor = '';
    };
  }, [selectionMode]);

  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (useSelectionStore.getState().mode !== 'drawing') return;
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      const current = useSelectionStore.getState().polygon;
      const last = current[current.length - 1];
      if (last) {
        const dx = last[0] - lng;
        const dy = last[1] - lat;
        if (Math.hypot(dx, dy) < 1e-5) return;
      }
      addVertex([lng, lat]);
    },
    [addVertex]
  );

  const handleMapDblClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const state = useSelectionStore.getState();
      if (state.mode !== 'drawing') return;
      e.preventDefault();
      if (!blob || state.polygon.length < 3) return;
      closePolygon(blob.data.positions);
      const next = useSelectionStore.getState().baseSelection;
      console.info(`[selection] enclosed ${next?.length ?? 0} dots`);
    },
    [blob, closePolygon]
  );

  const layers = useMemo(() => {
    const out: Layer[] = [];
    if (blob) {
      const { data, regions } = blob;
      const colorAttribute = fillColors
        ? { value: fillColors, size: 4, normalized: true }
        : undefined;
      out.push(
        new ScatterplotLayer({
          id: 'population',
          data: {
            length: data.length,
            attributes: {
              getPosition: { value: data.positions, size: 2 },
              ...(colorAttribute ? { getFillColor: colorAttribute } : {}),
            },
          },
          radiusUnits: 'meters',
          getRadius: 20,
          radiusMinPixels: 1,
          getFillColor: [COLOR_R, COLOR_G, COLOR_B, SELECTED_ALPHA],
          updateTriggers: {
            getFillColor: fillColors,
          },
          pickable: true,
          onHover: (info) => {
            if (useSelectionStore.getState().mode === 'drawing') {
              setHover(null);
              return;
            }
            if (info.index >= 0 && info.x != null && info.y != null) {
              const i = info.index;
              setHover({
                x: info.x,
                y: info.y,
                id: data.getId(i),
                name: data.getName(i),
                ageBand: data.ageBands[i],
                regionCode: regions[data.regionIndices[i]] ?? '?',
              });
            } else {
              setHover(null);
            }
          },
          onClick: (info) => {
            if (useSelectionStore.getState().mode === 'drawing') return;
            if (info.index >= 0) {
              selectPerson(data.getId(info.index));
            }
          },
        })
      );
    }
    if (polygon.length >= 3) {
      out.push(
        new PolygonLayer({
          id: 'selection-polygon',
          data: [{ contour: polygon }],
          getPolygon: (d: { contour: [number, number][] }) => d.contour,
          getFillColor:
            selectionMode === 'drawing' ? [37, 99, 235, 30] : [37, 99, 235, 50],
          getLineColor: [37, 99, 235, 220],
          getLineWidth: 2,
          lineWidthUnits: 'pixels',
          stroked: true,
          filled: true,
          pickable: false,
        })
      );
    }
    if (polygon.length > 0) {
      out.push(
        new ScatterplotLayer({
          id: 'selection-vertices',
          data: polygon,
          getPosition: (d: [number, number]) => d,
          getRadius: 4,
          radiusUnits: 'pixels',
          getFillColor: [255, 255, 255, 255],
          getLineColor: [37, 99, 235, 255],
          stroked: true,
          lineWidthUnits: 'pixels',
          getLineWidth: 2,
          pickable: false,
        })
      );
    }
    return out;
  }, [blob, fillColors, polygon, selectionMode, selectPerson]);

  return (
    <>
      <Map
        ref={mapRef}
        initialViewState={INITIAL_VIEW_STATE}
        mapStyle={env.VITE_BASEMAP_URL}
        style={{ width: '100%', height: '100%' }}
        attributionControl={true}
        dragPan={selectionMode !== 'drawing'}
        doubleClickZoom={selectionMode !== 'drawing'}
        onClick={handleMapClick}
        onDblClick={handleMapDblClick}
      >
        <DeckGLOverlay layers={layers} interleaved={false} />
      </Map>
      {hover && <HoverTooltip {...hover} />}
      {selectionMode === 'drawing' && (
        <div className="map-draw-hint" role="status" aria-live="polite">
          <strong>Drawing selection</strong>
          <span>
            Click to add vertices · double-click or Enter to close · Esc to
            cancel
          </span>
        </div>
      )}
      {loading && !blob && !error && (
        <div className="map-status" role="status" aria-live="polite">
          <span className="map-status__spinner" aria-hidden="true" />
          Loading population…
        </div>
      )}
      {!loading && !blob && !error && (
        <div className="map-status map-status--empty" role="status">
          No population data available.
        </div>
      )}
      {error && (
        <div className="map-error" role="alert">
          <strong>Could not load population data.</strong>
          <span className="map-error__detail">{error.message}</span>
          <button
            type="button"
            className="map-error__retry"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      )}
    </>
  );
}
